/**
 * DeliveryNotesModal Component
 * Modal untuk menambahkan catatan pengiriman (opsional)
 */

import {
  Box,
  chakra,
  Flex,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Icon } from '@chakra-ui/react';
import { Button, Text, TextField } from '@diamond/shared/ui';
import {
  NOTE_MAX_LENGTH_DEFAULT,
  sanitizeNote,
} from '@diamond/sol-buyer/utils';
import { EditOutlined } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';

export interface DeliveryNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryOption?: string;
  currentNote?: string;
}

/**
 * DeliveryNotesModal Component
 * Modal untuk input catatan tambahan pengiriman
 */
export function DeliveryNotesModal({
  isOpen,
  onClose,
  deliveryOption,
  currentNote,
}: DeliveryNotesModalProps) {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext();

  const deliveryNoteValue = watch('delivery_note');

  /**
   * Handle save (just close modal, form auto-updates)
   */
  const handleSave = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl" fontWeight="bold">
            Catatan Pengiriman
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Box>
            {/* Info Text */}
            <Text fontSize="sm" color="gray.600" mb={4} textAlign="justify">
              Jam pengantaran bergantung pada rute kurir dan keadaan lalu lintas
              pada hari pengiriman. Permintaan tanggal kirim pada Catatan Produk
              tidak berlaku.
            </Text>

            {/* Delivery Info based on option */}
            <Box
              bg="blue.50"
              p={3}
              borderRadius="md"
              mb={4}
              borderLeftWidth="4px"
              borderLeftColor="blue.500"
            >
              <Text fontSize="sm" color="gray.700">
                {deliveryOption === 'immediate'
                  ? 'Pembuatan PO dihari Senin - Jumat sebelum jam 15:00 atau Sabtu sebelum jam 12:00 akan dikirim H+1 hari kerja. Diluar jam tersebut, maka pesanan akan diproses dihari berikutnya.'
                  : 'Tanggal maksimal 5 hari kerja sejak pembuatan PO. Pengiriman hanya dilakukan pada hari kerja, Tidak ada pengiriman di hari minggu dan tanggal merah.'}
              </Text>
            </Box>

            {/* Catatan Tambahan Field */}
            <Box>
              <FormLabel
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                htmlFor="delivery_note"
                mb={2}
              >
                <Text fontWeight="medium">
                  Catatan Tambahan{' '}
                  <chakra.span fontSize="sm" color="gray.500">
                    (Opsional)
                  </chakra.span>
                </Text>
                <Icon as={EditOutlined} color="gray.400" boxSize={5} />
              </FormLabel>

              <TextField
                id="delivery_note"
                name="delivery_note"
                placeholder="Contoh: Antarkan ke loading dock mall"
                size="md"
                rounded="md"
                maxLength={NOTE_MAX_LENGTH_DEFAULT}
                register={register}
                registerOptions={{
                  maxLength: {
                    value: NOTE_MAX_LENGTH_DEFAULT,
                    message: 'Maksimal 150 karakter',
                  },
                  validate: (v: string) =>
                    /^[^\p{Cc}]*$/u.test(v) || 'Karakter tidak valid',
                }}
                errors={errors}
                onInput={(e) => {
                  const input = e.currentTarget as HTMLInputElement;
                  input.value = sanitizeNote(
                    input.value,
                    NOTE_MAX_LENGTH_DEFAULT
                  );
                }}
              />

              {/* Character count */}
              <Flex justifyContent="flex-end" mt={1}>
                <Text fontSize="xs" color="gray.500">
                  {deliveryNoteValue?.length || 0}/{NOTE_MAX_LENGTH_DEFAULT}
                </Text>
              </Flex>
            </Box>

            {/* Warning Text */}
            <Box mt={4}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: 0, md: 2 }}
              >
                <Text color="red.500" fontSize="sm" fontWeight="medium">
                  Bukan untuk ganti lokasi pengiriman.
                </Text>
                <Text color="gray.600" fontSize="sm">
                  Khusus catatan terkait penerima barang.
                </Text>
              </Flex>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Batal
          </Button>
          <Button variant="solid" onClick={handleSave}>
            Simpan
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
